/**
 * ThemeToggle — VÉRAULT ecosystem theme switch (Wave 30.4 · Dark Mode Refinement).
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Quiet moon/sun affordance for the portal top bar. Apple/Aman-inspired:
 * a single iconographic button, no menu, no popover. Click → flips
 * `data-theme` instantly with a 200ms cross-fade. Persists to localStorage
 * via the existing `useTheme()` hook so the choice survives reloads + works
 * across the public site, advisor portal, and Member Suite.
 *
 * Restraint: no label, no halo, no animation beyond a calm icon cross-fade.
 */
import React from "react";
import { Moon, Sun } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useTheme } from "../hooks/useTheme";

export default function ThemeToggle({ scope = "client" }) {
  const { theme, toggle } = useTheme(scope);
  const isDark = theme === "dark";
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={isDark ? "Switch to light theme" : "Switch to dark theme"}
      title={isDark ? "Light" : "Dark"}
      data-testid="theme-toggle"
      className="relative inline-flex items-center justify-center size-9 rounded-full text-[hsl(var(--foreground-secondary))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--surface-hover))] transition-colors"
    >
      <AnimatePresence initial={false} mode="wait">
        {isDark ? (
          <motion.span
            key="sun"
            initial={{ opacity: 0, rotate: -30, scale: 0.85 }}
            animate={{ opacity: 1, rotate: 0, scale: 1 }}
            exit={{ opacity: 0, rotate: 30, scale: 0.85 }}
            transition={{ duration: 0.2 }}
            className="inline-flex"
          >
            <Sun className="size-4" strokeWidth={1.5} />
          </motion.span>
        ) : (
          <motion.span
            key="moon"
            initial={{ opacity: 0, rotate: 30, scale: 0.85 }}
            animate={{ opacity: 1, rotate: 0, scale: 1 }}
            exit={{ opacity: 0, rotate: -30, scale: 0.85 }}
            transition={{ duration: 0.2 }}
            className="inline-flex"
          >
            <Moon className="size-4" strokeWidth={1.5} />
          </motion.span>
        )}
      </AnimatePresence>
    </button>
  );
}
